import arrayMove from 'array-move';
import isArray from 'lodash/isArray';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import map from 'lodash/map';
import merge from 'lodash/merge';
import uniq from 'lodash/uniq';
import * as comparableListings from 'actionCreators/comparableListings';
import * as comparableSales from 'actionCreators/comparableSales';
import * as comparableRentals from 'actionCreators/comparableRentals';
import * as ComparablesApi from 'api/ComparablesApi';
import * as DigitalAssetApi from 'api/DigitalAssetApi';
import { deleteAsset } from 'api/DigitalAssetApi';
import { updateFilters } from 'ducks/filters';
import { updateManyPropertyNotes } from 'ducks/propertyNotes';
import { updateUploadProgress } from 'ducks/uploadProgress';
import AnalyticsGateway from 'lib/AnalyticsGateway';
import * as ImageResizer from 'lib/ImageResizer';
import { getIn } from 'lib/immutableObject';
import { updatePropertyPreferences } from 'api/propertyPreferences';
import RapidBatchSearchApi from 'api/RapidBatchSearchApi';
import { updateReportAlert } from 'ducks/alerts';
import { getFilterStore } from 'zustandStore/useFilterStore';
import { UNLOADER } from '../constants/loader';
import { COMPARABLE_TYPE } from '../constants/comparableTypes';

const SEARCH_FAILURE = 'Search failed. Please try again.';

// TODO: Make this work again in the new filter search
export function getShowActiveListingsRentals(params) {
  const { userSettings, comparableType } = params;
  const preference = userSettings[`show.current.${comparableType}`];
  return preference !== undefined ? JSON.parse(preference) : true;
}

export function comparableTypeFromPageContext(pageContext) {
  switch (pageContext) {
    case 'Comparable Sales':
      return 'sales';
    case 'Comparable Listings':
      return 'listings';
    case 'Comparable Rentals':
      return 'rentals';
    default:
      throw new Error(`unexpected pageContext: ${pageContext}`);
  }
}

const comparableActions = {
  [COMPARABLE_TYPE.LISTINGS]: {actions: comparableListings, name: 'comparableListings'},
  [COMPARABLE_TYPE.SALES]: {actions: comparableSales, name: 'comparableSales'},
  [COMPARABLE_TYPE.RENTALS]: {actions: comparableRentals, name: 'comparableRentals'},
};

const name = (comparableType) => {
  return comparableActions[comparableType].name;
};

const getComparable = (state, comparableType) => {
  return state[name(comparableType)];
};

export const getSelected = (state, comparableType) => {
  return getComparable(state, comparableType).selected;
};

export const getFeatured = (state, comparableType) => {
  return getComparable(state, comparableType).featured;
};

export const getSelectedMoved = (state, comparableType) => {
  return comparableType ? getComparable(state, comparableType).selectedMoved : false;
};

export const getPage = (reportType, comparableType) => {
  return ['cma', 'rental'].includes(reportType.value) ? 'Comparable ' + capitalize(comparableType) : 'Statement of Information';
};

export const addComparableItem = (propertyId, selected, featured) => {
  if (featured.includes(propertyId)) {
    return [propertyId, ...selected.filter((s) => s !== propertyId)];
  }
  return [...selected.filter((s) => s !== propertyId), propertyId];
};

export const moveSelectedItem = (comparableType) => (oldIndex, newIndex) => {
  return (dispatch, getState) => {
    const {
      reportType,
      propertyDetail: { propertyId: targetPropertyId },
      [name(comparableType)]: {selected, featured},
    } = getState();
    let lastFeatureIndex = 0;
    while (featured.includes(selected[lastFeatureIndex])) lastFeatureIndex++;
    lastFeatureIndex--;

    if (featured.includes(selected[oldIndex])) {
      if (newIndex > oldIndex && newIndex > lastFeatureIndex) {
        newIndex = lastFeatureIndex;
      }
    } else {
      if (newIndex < oldIndex && newIndex <= lastFeatureIndex) {
        newIndex = lastFeatureIndex + 1;
      }
    }
    const moved = arrayMove(selected, oldIndex, newIndex);

    updatePropertyPreferences(targetPropertyId, {
      [name(comparableType) + '.selected']: moved,
      [name(comparableType) + '.selectedMoved']: true,
    });

    AnalyticsGateway.sendEvent({
      eventName: 'Change Comparable Order',
      properties: [
        {label: 'Page', value: getPage(reportType, comparableType)}
      ]
    });

    const comparableAction = comparableActions[comparableType];
    dispatch({type: comparableAction.actions.MOVE_SELECTED, payload: {oldIndex, newIndex}});
  };
};


export const orderSelectedItem = (comparableType) => (selected) => {
  const comparableAction = comparableActions[comparableType];

  return (dispatch) => {
    dispatch({type: comparableAction.actions.ORDER_SELECTED, payload: selected});
  };
};

export const updateSelectAllItems = (comparableType) => (payload) => {
  return (dispatch, getState) => {
    const {
      propertyDetail: {propertyId},
      [name(comparableType)]: {properties},
    } = getState();

    const comparableAction = comparableActions[comparableType];
    if (payload) {
      updatePropertyPreferences(propertyId, {
        [name(comparableType) + '.selected']: Object.keys(properties).map(id => parseInt(id, 10)),
      });
      dispatch({type: comparableAction.actions.SELECT_ALL});
    } else {
      updatePropertyPreferences(propertyId, {
        [name(comparableType) + '.selected']: null,
        [name(comparableType) + '.selectedMoved']: null
      });
      dispatch({type: comparableAction.actions.DESELECT_ALL});
    }
  };
};

export const featureItem = (comparableType) => (propertyId, savePreference = false) => {
  return (dispatch, getState) => {
    if (savePreference) {
      const {
        propertyDetail: {propertyId: targetPropertyId},
        [name(comparableType)]: {featured},
        [name(comparableType)]: {selected},
      } = getState();

      updatePropertyPreferences(targetPropertyId, {
        [name(comparableType) + '.featured']: [...featured, propertyId],
        [name(comparableType) + '.selected']: [propertyId, ...selected.filter(pid => pid !== propertyId)]
      });

      AnalyticsGateway.sendEvent({
        eventName: 'Select Featured Comparable',
        properties: [
          {label: 'Type of Comparable Featured', value: capitalize(comparableType)}
        ]
      });
    }
    const comparableAction = comparableActions[comparableType];
    dispatch({
      type: comparableAction.actions.FEATURE_PROPERTY,
      payload: {propertyId: propertyId, updateSelected: savePreference}
    });
  };
};

export const removeFeatureItem = (comparableType) => (propertyId) => {
  return (dispatch, getState) => {
    const {
      propertyDetail: {propertyId: targetPropertyId},
      [name(comparableType)]: {featured},
      [name(comparableType)]: {selected},
    } = getState();

    updatePropertyPreferences(targetPropertyId, {
      [name(comparableType) + '.featured']: featured.filter(id => id !== propertyId)
    });
    if (selected.includes(propertyId)) {
      updatePropertyPreferences(targetPropertyId, {
        [name(comparableType) + '.selected']: [...selected.filter(id => id !== propertyId), propertyId]
      });
    }
    const comparableAction = comparableActions[comparableType];
    dispatch({type: comparableAction.actions.REMOVE_FEATURE_PROPERTY, payload: propertyId});
  };
};

export const fetchComparableItems = (comparableType) => (propertyId, applyExisting) => {
  const comparableAction = comparableActions[comparableType];
  // Logic behind is for data update to apply existing filters when property type gets updated.
  const filters = getFilterStore()?.[applyExisting ? 'appliedFilters' : 'filters'][comparableType];

  return async (dispatch, getState) => {
    const state = getState();

    const {
      featureFlags: {enableUndisclosedPriceInUI},
      propertyDetail: {
        localityName,
        addressPostcode: postcode,
        singleLineAddress,
        addressState,
        latitude,
        longitude,
      },
      propertyDetailCache: {addressTown} = {},
    } = state;

    const comparableItems = state[comparableAction.name];
    const {pagination: {page}, sortBy, dirty, fetching} = comparableItems;

    if (fetching || !propertyId || !singleLineAddress || !dirty || !(latitude && longitude)) return;

    dispatch(comparableAction.actions.updateSuggestedItems());

    try {
      const {
        data: comparables = [],
        metadata: {
          offset: comparablePage = 0,
          totalElements = 0,
          totalPages = 0,
          limit: size = 10,
        } = {},
      } = (await RapidBatchSearchApi.getRapidBatchSearch({
        addressState,
        addressTown,
        comparableType,
        filters,
        localityName,
        postcode,
        page,
        sortBy,
        coordinates: { lat: latitude, lon: longitude },
        targetId: propertyId,
      }, comparableType)) || {};

      // these may have changed in the time it takes the fetch to respond, so getState() must be called again after fetch
      const {selected, suggested, properties} = getState()[comparableAction.name];

      const newProperties = Object.fromEntries(
          comparables.map(({ propertyNotes, ...comparable }) => {
            if (!enableUndisclosedPriceInUI && comparable.isPriceWithheld) {
              comparable.salePrice = 0;
            }
            return [comparable.propertyId, comparable];
          })
      );

      const selectedProperties = Object
          .fromEntries(selected.map(selected => [selected, properties[selected]]));

      const newSuggested = map(comparables, (comparable) => comparable.propertyId);

      const resultingProperties = comparablePage === 0 ?
          merge(newProperties, selectedProperties) :
          merge(cloneDeep(properties), newProperties, selectedProperties);

      const transformedComparables = {
        propertyId,
        fetching: false,
        suggested: uniq(comparablePage === 0 ? [...selected, ...newSuggested] : [...selected, ...suggested, ...newSuggested]),
        pagination: {page: comparablePage, size, totalElements, totalPages},
        properties: resultingProperties,
        dirty: false,
      };

      dispatch(comparableAction.actions.updateItems(transformedComparables));
      dispatch(updateFilters({errors: {}}));
      dispatch(updateManyPropertyNotes(comparables
          .filter(({propertyNotes}) => propertyNotes)
          .reduce((acc, {propertyId: comparablePropertyId, propertyNotes}) => ({
            ...acc,
            [comparablePropertyId]: propertyNotes
          }), {})));
    } catch (e) {
      // Invalid Search params
      if (getIn(e, ['response', 'status']) === 400) {
        dispatch(comparableAction.actions.updateItems({fetching: false, dirty: false}));
        dispatch(updateReportAlert({
          isShown: true,
          msg: SEARCH_FAILURE,
        }));
      } else if (getIn(e, ['response', 'status']) === 401) {
        dispatch(comparableAction.actions.updateItems({fetching: false, dirty: true}));
      } else {
        dispatch(comparableAction.actions.updateItems({fetching: false, dirty: false}));
        dispatch(updateFilters({errors: {request: SEARCH_FAILURE}}));
      }
    } finally {
      // This would hide the loader from ReportStepLayout.js
      dispatch({ type: UNLOADER });
    }
  };
};

export const uploadComparableItemImage = (comparableType) => (propertyId, file, featuredPhotoIndex = null) => {
  return async (dispatch, getState) => {
    const resizedFile = await ImageResizer.resize(file);
    dispatch(updateUploadProgress(0.1));
    const {data: {url}} = await DigitalAssetApi.uploadDigitalAsset({
      source: 'upload-property-image',
      propertyId: propertyId,
      file: resizedFile
    });

    const state = getState();
    const comparableAction = comparableActions[comparableType];
    const property = state[comparableAction.name].properties[propertyId];
    const index = property.propertyPhotoList.length;
    const readUrl = DigitalAssetApi.convertToReadUrl(url);
    dispatch(comparableAction.actions.updateForSession({
      propertyId,
      keyPath: ['propertyPhotoList', index],
      value: readUrl,
    }));
    dispatch(comparableAction.actions.updateForSession({
      propertyId,
      keyPath: ['uploadPhotoList'],
      value: [...(property.uploadPhotoList ?? []), readUrl],
    }));
    if (featuredPhotoIndex !== null) {
      let featuredPhotos = [...(property.featuredPhotos || []), null, null, null].slice(0, 3);
      featuredPhotos[featuredPhotoIndex] = readUrl;
      featuredPhotos = featuredPhotos.map((url, i) => (!url && i === property.propertyPhotoList.length) ? '' : url);

      dispatch(comparableAction.actions.updateForSession({
        propertyId,
        keyPath: ['featuredPhotos'],
        value: featuredPhotos,
      }));
    }
  };
};

export const deleteComparableItemImage = (comparableType) => (propertyId, photoIndex) => {
  return async (dispatch, getState) => {
    const comparableAction = comparableActions[comparableType];
    const state = getState();
    const propertyPhotoList = state[comparableAction.name].properties[propertyId].propertyPhotoList;
    const deleteUrl = propertyPhotoList[photoIndex];
    await deleteAsset(deleteUrl);

    dispatch(comparableAction.actions.updateForSession({
      propertyId,
      keyPath: ['propertyPhotoList'],
      value: propertyPhotoList.filter((v) => v !== deleteUrl),
    }));
  };
};

export const setDisplayItemField = (comparableType) =>
    ({
       propertyId,
       fieldName = '',
       descriptiveFieldName,
       page = 'Customise Report',
       visible,
     }) => {
      return (dispatch) => {
        if (!propertyId) {
          return Promise.resolve('No property ID found');
        }
        const comparableAction = comparableActions[comparableType];
        const fields = isArray(fieldName) ? fieldName : [fieldName];
        const displayFields = fields.reduce((acc, key) => ({
          ...acc,
          [key]: visible,
        }), {});

        dispatch({
          type: comparableAction.actions.SET_DISPLAY_FIELD,
          payload: {
            propertyId,
            displayFields,
          },
        });

        AnalyticsGateway.sendEvent({
          eventName: `Display ${descriptiveFieldName}`,
          properties: [
            { label: 'Page', value: page },
            { label: 'Comparable Property ID', value: propertyId },
            { label: descriptiveFieldName, value: visible ? 'show' : 'hide' },
          ],
        });
      };
    };

// TODO: this needs to load in the call for property photos
export const resetComparableItemFields = (comparableType) => (
    {
      propertyId,
      updateFields = [],
    }
) => {
  const apiName = {
    sales: 'getComparableSales',
    listings: 'getComparableListings',
    rentals: 'getComparableRentals',
  }[comparableType];
  const {actions} = comparableActions[comparableType];
  return async (dispatch, getState) => {
    if (!propertyId) {
      return Promise.resolve('No property ID found');
    }
    const {
      featureFlags: {enableUndisclosedPriceInUI},
      propertyDetail: {propertyId: targetPropertyId, latitude, longitude} = {},
    } = getState();

    const fetchingFields = updateFields.reduce((acc, field) => ({
      [field]: true,
    }), {});
    dispatch(actions.updateFetching({propertyId, fetching: fetchingFields}));

    const updatePreferences = updateFields
        .map(field => `comparable${capitalize(comparableType)}.properties.${propertyId}:${JSON.stringify(field)}`)
        .reduce((result, key) => {
          result[key] = null;
          return result;
        }, {});
    await updatePropertyPreferences(targetPropertyId, updatePreferences);

    // Copy the behavior from fetchItem
    const data = await ComparablesApi[apiName](targetPropertyId, propertyId, latitude, longitude);
    const mappedProperty = [...updateFields, 'propertyId']
        .reduce((result, field) => {
          result[field] = data[field];
          return result;
        }, {});
    AnalyticsGateway.sendEvent({
      eventName: 'Reset Comparable Property',
      properties: [
        { label: 'Page', value: 'Customise Report' },
        { label: 'Comparable Property ID', value: propertyId },
      ],
    });

    const resetFetchingFields = Object.keys(fetchingFields)
        .reduce((acc, key) => ({...acc, [key]: false}), {});
    return Promise.all([
      dispatch({ type: actions.RESET_COMPARABLE, flags: {enableUndisclosedPriceInUI}, payload: mappedProperty }),
      dispatch(actions.updateFetching({propertyId, fetching: resetFetchingFields})),
    ]);
  };
};

export const updateComparableItemFetching = (comparableType) => (
    {
      propertyId,
      fetching = {},
    } = {}
) => {
  const {actions} = comparableActions[comparableType];
  return (dispatch) => {
    if (!propertyId) {
      return Promise.reject('Expected a Property ID');
    } else if (!fetching || typeof fetching === 'string') {
      return Promise.reject('Fetching field should be an object');
    }
    const payload = { propertyId, fetching };
    return dispatch({type: actions.UPDATE_ITEM_FETCHING, payload});
  };
};
